import type { CountryCode } from 'shared/config/countries';
import type { ProjectCapabilities, ProjectFeatureFlags } from 'shared/utils/feature-flags';

export type { CountryCode, ProjectCapabilities, ProjectFeatureFlags };

export {
  FEATURE_FLAG_KEYS,
  FEATURE_FLAG_LABELS,
  INVERTED_FEATURE_FLAGS,
  flagToApi,
  flagToUi,
} from 'shared/utils/feature-flags';

/* The amounts a sub-project's units carry, in UF, by subsidy type: the project's `subsidyConfig` column.
   Only the DS19 today, mirroring PROJECT_CONFIGURABLE_SUBSIDY_TYPES in the backend: it belongs to the unit,
   so the client cannot declare it. The other subsidies are the client's to state. */
export interface ProjectSubsidyConfig {
  ds19?: ProjectSubsidyTypeConfig;
}

/* What a sub-project sets for one subsidy type, in UF. An absent ceiling runs on the type's default. */
export interface ProjectSubsidyTypeConfig {
  amountUF?: number;
  maxPropertyValueUF?: number;
}

export interface CreateMasterSubProjectInput extends Partial<ProjectFeatureFlags> {
  name: string;
  prompt: string;
  promptTypeId?: string;
}

export interface CreateMasterPayload {
  master: ProjectFeatureFlags & {
    name: string;
    phoneNumber: string;
    countryCode: CountryCode;
    commonPrompt?: string;
    pusherIdentification?: string;
  };
  subProjects: CreateMasterSubProjectInput[];
}

export interface CreateMasterResponse {
  id: number;
  name: string;
  masterProjectId: number | null;
  subProjects?: Array<{ id: number; name: string; masterProjectId: number | null }>;
  [key: string]: unknown;
}

export interface OpenAIFunctionParameterProperty {
  type: string;
  description?: string;
  enum?: string[];
  items?: {
    type: string;
    enum?: string[];
  };
}

export interface OpenAIFunctionDefinition {
  type: 'function';
  function: {
    name: string;
    description: string;
    parameters?: {
      type: string;
      properties: Record<string, OpenAIFunctionParameterProperty>;
      required?: string[];
    };
  };
}

export interface WhatsAppProperties {
  d360ChannelId: string | null;
  d360ApiKey: string | null;
  webhookActive?: boolean;
}

export interface ProjectDetail extends ProjectFeatureFlags {
  id: number;
  name: string;
  masterProjectId: number | null;
  countryCode?: CountryCode | null;
  devUrl?: string | null;
  pusherIdentification?: string | null;
  openAIModel?: string | null;
  phoneNumber?: string | null;
  whatsAppProperties?: WhatsAppProperties | null;
  whatsAppBusinessAccountId?: string | null;
  whatsAppFromPhoneNumberId?: string | null;
  whatsAppToken?: string | null;
  commonPrompt?: string | null;
  prompt?: string | null;
  /* Agent prompts hold a `<GLOBALSECTION:name>` tag, not the prompt itself: the text lives in the
     section and the backend resolves the tag. Null means the agent runs the global default. */
  mortgageAgentPrompt?: string | null;
  financialAdvisorAgentPrompt?: string | null;
  subsidyConfig?: ProjectSubsidyConfig | null;
  /* Masters only: a financial entity attending its own leads (Lead Management), with or without sub-projects. */
  isFinancialEntity?: boolean;
  description?: string | null;
  commune?: string | null;
  financialScoreWeight?: number | null;
  aiScoreWeight?: number | null;
  minimumDownPaymentRate?: number | null;
  assignmentAgentEnabled?: boolean | null;
  hasFunnel?: boolean | null;
  alertsConfig?: Array<{
    id?: string;
    description?: string;
    examples?: string[];
    actions?: unknown[];
  }> | null;
  openAIFunctions?: OpenAIFunctionDefinition[];
}

export interface UpdateStockAndPricesPayload {
  id: number;
  dryRun?: boolean;
  forceUpdate?: boolean;
}

export interface StockUpdateStepResult {
  success: boolean;
  endpoint: string;
  response: unknown;
  error?: string;
  strategy?: string;
}

export interface UpdateStockAndPricesResponse {
  integrationType: string;
  stockUpdate: StockUpdateStepResult;
  minPricesUpdate: StockUpdateStepResult;
}

export interface UpdateMinPricesResponse {
  minPricesUpdate: StockUpdateStepResult;
}

export interface MasterDetail extends ProjectDetail {
  subProjects?: ProjectDetail[];
}

export interface UpdateProjectPayload {
  id: number;
  // `changeReason` / `promptLabel` are accepted by PATCH /admin/projects/:id when
  // the patch includes `prompt`/`commonPrompt` (creates a PromptVersion). Optional,
  // so existing attribute-only callers are unaffected.
  patch: Partial<ProjectDetail> & {
    changeReason?: string;
    promptLabel?: 'production' | 'latest';
    /** Model saved on the new prompt version. With promptLabel production, a model other than the project's goes live. */
    promptModel?: string;
  };
}

export type MasterIdentityPatch = Partial<
  Pick<ProjectDetail, 'name' | 'countryCode' | 'phoneNumber' | 'pusherIdentification' | 'devUrl'>
>;

export type MasterPhonePatch = {
  phoneNumber?: string;
  whatsAppProperties?: WhatsAppProperties | null;
  whatsAppBusinessAccountId?: string | null;
  whatsAppFromPhoneNumberId?: string | null;
  whatsAppToken?: string | null;
};

export type WhatsAppQualityRating = 'GREEN' | 'YELLOW' | 'RED';

export type WhatsAppMessagingLimitTier =
  | 'TIER_50'
  | 'TIER_250'
  | 'TIER_1K'
  | 'TIER_2K'
  | 'TIER_10K'
  | 'TIER_100K'
  | 'TIER_UNLIMITED';

export interface ProjectPhoneConfig {
  phoneNumber: string | null;
  whatsAppProperties: WhatsAppProperties | null;
  whatsAppQualityRating: WhatsAppQualityRating | null;
  whatsAppMessagingLimitTier: WhatsAppMessagingLimitTier | null;
}

export interface UpdateWhatsAppHealthResult {
  projectId: number;
  projectName?: string;
  success: boolean;
  healthInfo?: {
    qualityRating: WhatsAppQualityRating | null;
    messagingLimitTier: WhatsAppMessagingLimitTier | null;
  } | null;
  error?: string;
}

/**
 * POST /templates/update-whatsAppHealth. Top-level `success` only means the request found projects to
 * process — the per-project outcome lives in `results`, so callers must read that to detect failures.
 */
export interface UpdateWhatsAppHealthResponse {
  success: boolean;
  message: string;
  total: number;
  successful: number;
  failed: number;
  results: UpdateWhatsAppHealthResult[];
}

export type MasterCapabilitiesPatch = Partial<ProjectCapabilities>;

export type MasterParametersPatch = Pick<
  Partial<ProjectDetail>,
  'financialScoreWeight' | 'aiScoreWeight' | 'minimumDownPaymentRate'
>;

export type MasterAlertsPatch = Pick<Partial<ProjectDetail>, 'alertsConfig'>;

export interface InitializeMasterFunnelResponse {
  success: boolean;
  message: string;
  data?: unknown;
}

export interface CreateSubProjectPayload extends ProjectFeatureFlags {
  name: string;
  prompt: string;
  commune?: string;
  phoneNumber: string;
  countryCode: CountryCode;
  masterProjectId: number;
  pusherIdentification?: string;
  devUrl: string;
  scheduleVisitInstructions: string;
  availableDates: never[];
  askClaveUnica: boolean;
  disableResponseDelay: boolean;
  guardrailMode: 'disabled';
}

export const MIGRATION_ENTITIES = [
  'allProperties',
  'subProjects',
  'imagesAndFiles',
  'minimumUFPrice',
  'betaScores',
  'functionalities',
  'alerts',
  'propertyTypes',
  'prompt',
  'promptDescriptions',
  'ruleConfigs',
  'promptSections',
  'targetProject',
  'location',
] as const;

export type MigrationEntity = (typeof MIGRATION_ENTITIES)[number];

export const MIGRATION_ENTITY_LABELS: Record<MigrationEntity, string> = {
  allProperties: 'Migrar Todo',
  subProjects: 'Crear Sub-Proyectos',
  imagesAndFiles: 'Imágenes, Cotizaciones y Archivos',
  minimumUFPrice: 'Precio mínimo UF',
  betaScores: 'Betas de puntajes',
  functionalities: 'Funcionalidades',
  alerts: 'Alertas',
  propertyTypes: 'Tipos de propiedades',
  prompt: 'Prompt',
  promptDescriptions: 'Descripciones de sub-proyecto',
  ruleConfigs: 'Automatizaciones',
  promptSections: 'Prompt Sections',
  targetProject: 'Crear Proyecto',
  location: 'Dirección y Coordenadas',
};

export type ClonePayload =
  | {
      mode: 'same-master';
      sourceProjectId: number;
      newSubProjectName?: string;
      entitiesToMigrate: MigrationEntity[];
      dryRun?: boolean;
    }
  | {
      mode: 'different-master';
      sourceProjectId: number;
      targetProjectId?: number;
      /** `modelSource`: 'default' saves the model of new projects; 'source' copies the source's model. */
      targetProject?: { name: string; phoneNumber?: string; modelSource?: 'default' | 'source' };
      subProjectIds?: number[];
      entitiesToMigrate: MigrationEntity[];
      dryRun?: boolean;
    };

export interface CloneStep {
  entity: MigrationEntity;
  status: 'pending' | 'validating' | 'executing' | 'verifying' | 'completed' | 'error';
  message: string;
  details?: Record<string, unknown>;
  createdAt: string;
  completedAt?: string;
}

export interface CloneResponse {
  success: boolean;
  operationId: string;
  steps: CloneStep[];
  summary: {
    totalSteps: number;
    completedSteps: number;
    failedSteps: number;
    objectsCreated: number;
    objectsUpdated: number;
    objectsSkipped: number;
  };
}
